/**
 * 엑셀/스프레드시트 표 붙여넣기 서식 정규화
 *
 * 엑셀은 셀 서식을 CSS 인라인 스타일(color/background/text-align 등)로 내보내지만,
 * BlockNote 테이블 셀은 자기 속성(data-text-color / data-background-color /
 * data-text-alignment)만 파싱하고 임의의 CSS 색은 무시한다.
 * 이 모듈은 엑셀 HTML을 BlockNote가 인식하는 형태로 변환한다:
 *  - CSS color/background → 가장 가까운 BlockNote 팔레트 색 → data-* 속성
 *  - text-align / align → data-text-alignment
 *  - font-weight/font-style/underline → <strong>/<em>/<u> 래핑 (마크 보존 강화)
 *
 * BlockNote 색은 고정 팔레트(10색)뿐이라 임의 hex는 "근사치"로 양자화된다.
 */

type RGB = [number, number, number];

const NAMED_COLORS: Record<string, string> = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  orange: "#ffa500",
  purple: "#800080",
  gray: "#808080",
  grey: "#808080",
};

/** CSS 색 문자열(#hex, rgb(), 일부 named)을 RGB로 파싱. 실패 시 null */
export function parseCssColorToRgb(input?: string | null): RGB | null {
  if (!input) return null;
  const s = input.trim().toLowerCase();
  if (!s || s === "transparent" || s === "none" || s === "inherit") return null;

  let m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (m) {
    let h = m[1];
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }

  m = s.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const parts = m[1].split(",").map((x) => parseFloat(x.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => !isNaN(n))) {
      return [parts[0], parts[1], parts[2]] as RGB;
    }
    return null;
  }

  if (NAMED_COLORS[s]) return parseCssColorToRgb(NAMED_COLORS[s]);
  return null;
}

/** RGB(0-255) → HSL. h:0-360, s:0-1, l:0-1 */
function rgbToHsl([r, g, b]: RGB): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / d + 2) * 60;
        break;
      default:
        h = ((r - g) / d + 4) * 60;
        break;
    }
  }
  return [h, s, l];
}

// 유채색 팔레트의 기준 색상(hue). TEXT_COLORS의 채도 높은 hex 기준.
const HUE_REFERENCE: { value: string; hue: number }[] = [
  { value: "red", hue: 0 },
  { value: "brown", hue: 17 },
  { value: "orange", hue: 30 },
  { value: "yellow", hue: 46 },
  { value: "green", hue: 150 },
  { value: "blue", hue: 197 },
  { value: "purple", hue: 262 },
  { value: "pink", hue: 324 },
];

function hueDist(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * 임의 색 → BlockNote 팔레트 value (hue 기반 매칭).
 * 무채색(낮은 채도)은 검정류=default, 그 외 회색=gray.
 */
function paletteValueFromRgb(rgb: RGB): string {
  const [h, s, l] = rgbToHsl(rgb);
  if (s < 0.15) {
    // 무채색
    if (l < 0.35) return "default"; // 검정류
    if (l > 0.85) return "default"; // 흰색류(무배경/기본 글자배경)
    return "gray";
  }
  let best = "gray";
  let bestDist = Infinity;
  for (const ref of HUE_REFERENCE) {
    const d = hueDist(h, ref.hue);
    if (d < bestDist) {
      bestDist = d;
      best = ref.value;
    }
  }
  return best;
}

/** 임의 색 → 가장 가까운 텍스트 팔레트 value (검정류는 default) */
export function nearestTextColorValue(rgb: RGB): string {
  return paletteValueFromRgb(rgb);
}

/** 임의 색 → 가장 가까운 배경 팔레트 value (흰색/근사 흰색은 default=무배경) */
export function nearestBackgroundColorValue(rgb: RGB): string {
  const [, s, l] = rgbToHsl(rgb);
  // 흰색/근사 흰색은 배경 없음으로 간주
  if (s < 0.12 && l > 0.85) return "default";
  return paletteValueFromRgb(rgb);
}

/** "k:v; k2:v2" → { k: v } */
function parseStyle(style: string): Record<string, string> {
  const out: Record<string, string> = {};
  style.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const k = decl.slice(0, idx).trim().toLowerCase();
    const v = decl.slice(idx + 1).trim();
    if (k) out[k] = v;
  });
  return out;
}

/** background 단축 속성에서 색만 추출 시도 */
function colorFromBackgroundShorthand(value: string): RGB | null {
  if (!value) return null;
  const direct = parseCssColorToRgb(value);
  if (direct) return direct;
  for (const token of value.split(/\s+/)) {
    const rgb = parseCssColorToRgb(token);
    if (rgb) return rgb;
  }
  return null;
}

/** rgba alpha=0 / transparent 여부 */
function isTransparentColor(css?: string | null): boolean {
  if (!css) return true;
  const s = css.trim().toLowerCase();
  if (s === "transparent" || s === "none") return true;
  const m = s.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const p = m[1].split(",").map((x) => parseFloat(x.trim()));
    if (p.length >= 4 && p[3] === 0) return true;
  }
  return false;
}

/** CSS text-align 값을 BlockNote 정렬 value로 (left/start는 기본이라 빈 문자열) */
function normalizeAlign(ta?: string | null): string {
  const v = (ta || "").trim().toLowerCase();
  if (v === "right" || v === "end") return "right";
  if (v === "center") return "center";
  if (v === "justify") return "justify";
  return "";
}

/** CSS vertical-align → BlockNote verticalAlignment (top/기본은 null로 스킵) */
function mapVerticalAlign(v?: string | null): "middle" | "bottom" | null {
  const s = (v || "").trim().toLowerCase();
  if (s === "middle" || s === "center") return "middle";
  if (s === "bottom") return "bottom";
  return null;
}

/** font-size 문자열을 px로. computed는 px, inline은 pt/px. 실패 시 null. */
function fontSizeToPx(raw?: string | null): number | null {
  if (!raw) return null;
  const m = String(raw).trim().match(/^([\d.]+)\s*(px|pt)?$/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (!Number.isFinite(v) || v <= 0) return null;
  return (m[2] || "px").toLowerCase() === "pt" ? v * (96 / 72) : v;
}

interface CellFormat {
  bgRgb: RGB | null;
  bgTransparent: boolean;
  colorRgb: RGB | null;
  align: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  /** 셀 글자 크기(px). 본문 기본(14px) 근처면 적용하지 않는다. */
  fontSizePx: number | null;
  /** 셀 세로 정렬(middle/bottom만; top/기본은 null). */
  verticalAlign: "middle" | "bottom" | null;
}

/** 셀 서식을 BlockNote data-* 속성/마크로 적용 */
function applyCellFormatting(el: HTMLElement, fmt: CellFormat): void {
  if (fmt.bgRgb && !fmt.bgTransparent) {
    const v = nearestBackgroundColorValue(fmt.bgRgb);
    if (v !== "default") el.setAttribute("data-background-color", v);
  }
  if (fmt.colorRgb) {
    const v = nearestTextColorValue(fmt.colorRgb);
    if (v !== "default") el.setAttribute("data-text-color", v);
  }
  if (["right", "center", "justify"].includes(fmt.align)) {
    el.setAttribute("data-text-alignment", fmt.align);
  }
  if ((fmt.bold || fmt.italic || fmt.underline) && el.innerHTML.trim()) {
    let inner = el.innerHTML;
    if (fmt.underline) inner = `<u>${inner}</u>`;
    if (fmt.italic) inner = `<em>${inner}</em>`;
    if (fmt.bold) inner = `<strong>${inner}</strong>`;
    el.innerHTML = inner;
  }
  // 세로 정렬: VerticalAlignmentExtension parseHTML이 data-vertical-alignment를 읽는다.
  if (fmt.verticalAlign) {
    el.setAttribute("data-vertical-alignment", fmt.verticalAlign);
  }
  // 글자 크기: 본문 기본(14px)과 1px 넘게 다르면 셀 내용을 FontSize 스타일 span으로 래핑.
  // (FontSize 스타일은 data-style-type/data-value 형태로 붙여넣기 왕복됨)
  if (
    fmt.fontSizePx &&
    Math.abs(fmt.fontSizePx - 14) > 1 &&
    el.innerHTML.trim()
  ) {
    const v = `${Math.round(fmt.fontSizePx)}px`;
    el.innerHTML =
      `<span data-style-type="fontSize" data-value="${v}" style="font-size:${v}">` +
      el.innerHTML +
      `</span>`;
  }
}

/** getComputedStyle로 셀 서식 읽기 (클래스/<style>/inline 모두 반영) */
function readComputedFormat(el: HTMLElement): CellFormat {
  const cs = getComputedStyle(el);
  const fw = cs.fontWeight;
  const fwNum = parseInt(fw, 10);
  const decoration = cs.textDecorationLine || cs.textDecoration || "";
  return {
    bgRgb: parseCssColorToRgb(cs.backgroundColor),
    bgTransparent: isTransparentColor(cs.backgroundColor),
    colorRgb: parseCssColorToRgb(cs.color),
    align: normalizeAlign(cs.textAlign),
    bold: fw === "bold" || fw === "bolder" || (!isNaN(fwNum) && fwNum >= 600),
    italic: (cs.fontStyle || "").toLowerCase().includes("italic"),
    underline: decoration.toLowerCase().includes("underline"),
    fontSizePx: fontSizeToPx(cs.fontSize),
    // ⚠️ computed vertical-align은 td 기본값이 "middle"이라 셀마다 잘못 붙는다.
    // 명시적 inline style / valign 속성만 읽는다(기본값 노이즈 방지).
    verticalAlign: mapVerticalAlign(
      el.style?.verticalAlign || el.getAttribute("valign"),
    ),
  };
}

/** inline style 속성만으로 셀 서식 읽기 (computed 불가 환경 폴백) */
function readInlineFormat(el: HTMLElement): CellFormat {
  const sm = parseStyle(el.getAttribute("style") || "");
  const bgRaw = sm["background-color"] || sm["background"] || "";
  const bgRgb =
    colorFromBackgroundShorthand(bgRaw) ||
    parseCssColorToRgb(el.getAttribute("bgcolor"));
  const fw = (sm["font-weight"] || "").toLowerCase();
  const decoration = sm["text-decoration"] || sm["text-decoration-line"] || "";
  return {
    bgRgb,
    bgTransparent: !bgRaw && !el.getAttribute("bgcolor"),
    colorRgb: parseCssColorToRgb(sm["color"]),
    align: normalizeAlign(sm["text-align"] || el.getAttribute("align")),
    bold: fw === "bold" || fw === "bolder" || parseInt(fw, 10) >= 600,
    italic: (sm["font-style"] || "").toLowerCase().includes("italic"),
    underline: decoration.toLowerCase().includes("underline"),
    fontSizePx: fontSizeToPx(sm["font-size"]),
    verticalAlign: mapVerticalAlign(
      sm["vertical-align"] || el.getAttribute("valign"),
    ),
  };
}

/**
 * 엑셀 표 HTML을 BlockNote가 인식하는 서식으로 정규화한다.
 *
 * 엑셀(데스크톱)은 셀 서식을 인라인 style이 아니라 <style> 블록의 클래스
 * (.xlNN { background:#FFFF00 })로 내보내는 경우가 많다. 따라서 표를 격리된
 * Shadow DOM(오프스크린)에 잠깐 넣고 getComputedStyle로 최종 서식을 읽어
 * 인라인·클래스·mso 형식을 모두 일괄 처리한다. Shadow DOM이라 엑셀 <style>이
 * 페이지로 누출되지 않는다. 실패/비브라우저 환경에서는 inline 파싱으로 폴백.
 */
export function normalizeExcelTableHtml(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script").forEach((s) => s.remove());
    if (!doc.querySelector("table")) return html;

    // 우선 경로: 실제 DOM + getComputedStyle
    if (
      typeof document !== "undefined" &&
      document.body &&
      typeof HTMLElement !== "undefined" &&
      typeof HTMLElement.prototype.attachShadow === "function"
    ) {
      let host: HTMLElement | null = null;
      try {
        host = document.createElement("div");
        host.setAttribute("aria-hidden", "true");
        host.style.cssText =
          "position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;font-size:14px";
        const shadow = host.attachShadow({ mode: "open" });
        const styles = Array.from(doc.querySelectorAll("style"))
          .map((s) => s.outerHTML)
          .join("");
        shadow.innerHTML = styles + doc.body.innerHTML;
        document.body.appendChild(host);

        shadow.querySelectorAll("td, th").forEach((node) => {
          applyCellFormatting(node as HTMLElement, readComputedFormat(node as HTMLElement));
        });

        const out = Array.from(shadow.querySelectorAll("table"))
          .map((t) => t.outerHTML)
          .join("");
        return out || html;
      } finally {
        if (host && host.parentNode) host.parentNode.removeChild(host);
      }
    }

    // 폴백 경로: inline style만 파싱
    doc.querySelectorAll("td, th").forEach((node) => {
      applyCellFormatting(node as HTMLElement, readInlineFormat(node as HTMLElement));
    });
    return (
      Array.from(doc.querySelectorAll("table"))
        .map((t) => t.outerHTML)
        .join("") || html
    );
  } catch {
    return html;
  }
}
